import React, { useState, useEffect } from 'react';
import { Activity, Target, DollarSign, Users, Award, X } from 'lucide-react';
import { adminAPI, userAPI } from '../../services/api';
import { formatCurrency } from '../../utils/calculations';

const MarketingStats = ({ onClose }) => {
  const [marketingStats, setMarketingStats] = useState({
    totalBets: 0,
    winRate: 0.0,
    totalVolume: 0,
    engagement: 0.0,
    activeUsers: 0,
    last30DaysBets: 0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadMarketingStats();
  }, []);

  const loadMarketingStats = async () => {
    try {
      setLoading(true);
      console.log('🔄 Carregando estatísticas de marketing com dados reais...');
      
      // Load all users to calculate stats
      const usersResponse = await userAPI.getAllUsers();
      const users = usersResponse || [];
      
      // Calculate active users (not expired)
      const now = new Date();
      const activeUsers = users.filter(user => {
        const keyExpires = new Date(user.key_expires_at);
        return keyExpires > now;
      }).length;

      // Buscar dados reais de apostas de todos os usuários
      let allBets = [];
      let totalBets = 0;
      let greenBets = 0;
      let totalVolume = 0;
      let last30DaysBets = 0;

      console.log(`📊 Processando ${users.length} usuários...`);

      for (const user of users) {
        try {
          const betsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bets?user_id=${user.id}`);
          if (betsResponse.ok) {
            const userBets = await betsResponse.json();
            allBets = [...allBets, ...userBets];
            
            totalBets += userBets.length;
            greenBets += userBets.filter(bet => bet.result === 'green').length;
            totalVolume += userBets.reduce((sum, bet) => sum + (bet.stake || 0), 0);

            // Últimos 30 dias
            const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            const recentBets = userBets.filter(bet => new Date(bet.date) >= thirtyDaysAgo);
            last30DaysBets += recentBets.length;
          }
        } catch (error) {
          console.error(`Erro ao carregar apostas do usuário ${user.id}:`, error);
        }
      }

      const winRate = totalBets > 0 ? (greenBets / totalBets) * 100 : 0;
      const engagement = users.length > 0 ? (activeUsers / users.length) * 100 : 0;

      console.log(`✅ Dados carregados: ${totalBets} apostas, ${greenBets} green, ${totalVolume} volume`);

      setMarketingStats({
        totalBets: totalBets,
        winRate: winRate,
        totalVolume: totalVolume,
        engagement: engagement,
        activeUsers: activeUsers,
        last30DaysBets: last30DaysBets
      });
    } catch (error) {
      console.error('Error loading marketing stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMarketingText = () => {
    return `"${marketingStats.totalBets} apostas registradas • ${marketingStats.winRate.toFixed(2)}% de taxa de acerto • ${formatCurrency(marketingStats.totalVolume)} em volume • ${marketingStats.activeUsers} usuários ativos"`;
  };

  return (
    <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 shadow-lg text-white mb-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold">📊 Estatísticas de Marketing</h3>
          <p className="text-purple-100 text-sm">Dados para divulgação e marketing da plataforma</p>
        </div>
        <div className="flex items-center space-x-3">
          <Award className="w-8 h-8 text-purple-200" />
          <button 
            onClick={onClose}
            className="text-purple-200 hover:text-white transition-colors" 
            title="Fechar"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-purple-100">📊 Carregando dados reais de todos os usuários...</p>
          <p className="text-purple-200 text-sm mt-2">Processando apostas, volumes e estatísticas</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <Activity className="w-6 h-6 text-purple-200" />
                <span className="font-medium text-purple-100">Total de Apostas</span>
              </div>
              <p className="text-3xl font-bold">{marketingStats.totalBets}</p>
              <p className="text-sm text-purple-200">Registradas na plataforma</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <Target className="w-6 h-6 text-green-300" />
                <span className="font-medium text-purple-100">Taxa de Acerto</span>
              </div>
              <p className="text-3xl font-bold text-green-300">{marketingStats.winRate.toFixed(2)}%</p>
              <p className="text-sm text-purple-200">Média geral dos usuários</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <DollarSign className="w-6 h-6 text-yellow-300" />
                <span className="font-medium text-purple-100">Volume Total</span>
              </div>
              <p className="text-3xl font-bold text-yellow-300">{formatCurrency(marketingStats.totalVolume)}</p>
              <p className="text-sm text-purple-200">Movimentado pelos usuários</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <Users className="w-6 h-6 text-blue-300" />
                <span className="font-medium text-purple-100">Engajamento</span>
              </div>
              <p className="text-3xl font-bold text-blue-300">{marketingStats.engagement.toFixed(2)}%</p>
              <p className="text-sm text-purple-200">Usuários ativos</p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-white/10 backdrop-blur-sm rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-bold text-lg">🎯 Dados para Marketing:</h4>
                <p className="text-purple-100 text-sm">{getMarketingText()}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-purple-200">Últimos 30 dias:</p>
                <p className="font-bold text-lg">{marketingStats.last30DaysBets} apostas</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MarketingStats;